import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Platform,
  ActivityIndicator,
  ScrollView,
  FlatList,
  TextInput,
} from 'react-native';
import { InsetModal as Modal } from '../components/InsetModal';
import { Ionicons } from '@expo/vector-icons';
import { ScreenLayout, Header, FormHeader, Input, Button, NotesSection, Note } from '../components';
import { ProductSelectorModal } from '../components/catalogues/ProductSelectorModal';
import { ContactSelector } from '../components/ContactSelector';
import { MapLocationPicker } from '../components/MapLocationPicker';
import { apiService } from '../services/api';
import { useAppStore } from '../store/appStore';
import { useAppContext } from '../contexts/AppContext';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { colors, spacing, typography } from '../theme';
import { designTokens } from '../theme/design-system';
import { CurrencyPicker } from '../components/design-system';
import { convertAmount, getExchangeRate } from '../utils/currencyConverter';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { usePhoneFormGutter } from '../hooks/usePhoneFormGutter';
import { useRTLStyles } from '../hooks/useRTLStyles';
import { useLocale } from '../contexts/LocaleContext';
import { businessCurrency } from '../utils/businessDefaults';

interface QuotationFormScreenProps {
  quotationId?: string;
}

interface QuotationLineItem {
  productId: string; // real id, or 'external' for a custom line
  variantId?: string;
  variant?: { id: string; name: string; price: number };
  priceUnit?: string;
  unitId?: string;
  name: string;
  sku?: string;
  productType?: 'physical' | 'digital' | 'service';
  quantity: number;
  unitPrice: number;
  totalPrice: number;
}

interface ExtraCharge {
  name: string;
  amount: number;
  _isTax?: boolean;
  _optional?: boolean;
}

/** Backend stores `notes` as a single string; the mobile app JSON-serializes an
 * array of authored Note entries into it (mirrors AddOrderScreen's exact
 * convention) so NotesSection can be reused here without a schema change. */
const parseNotes = (raw: any): Note[] => {
  if (!raw) return [];
  if (Array.isArray(raw)) {
    return raw.map((note: any) => (typeof note === 'string'
      ? { content: note, createdBy: { id: '', email: '' }, createdAt: new Date() }
      : { ...note, createdAt: note.createdAt ? new Date(note.createdAt) : new Date() }));
  }
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return parsed.map((note: any) => ({ ...note, createdAt: note.createdAt ? new Date(note.createdAt) : new Date() }));
      }
    } catch {
      return [{ content: raw, createdBy: { id: '', email: '' }, createdAt: new Date() }];
    }
  }
  return [];
};

export const QuotationFormScreen: React.FC<QuotationFormScreenProps> = ({ quotationId }) => {
  const isEdit = !!quotationId;
  const styles = useThemedStyles(getStyles);
  const phoneFormGutter = usePhoneFormGutter();
  const rtl = useRTLStyles();
  const { t } = useLocale();
  const { navigateTo, activeTab, setActiveTab, showNotification, routeParams } = useAppContext();
  const { user, currentBusiness } = useAppStore();

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [showProductPicker, setShowProductPicker] = useState(false);
  const [showCustomLineForm, setShowCustomLineForm] = useState(false);
  const [customLineName, setCustomLineName] = useState('');
  const [customLinePrice, setCustomLinePrice] = useState('');
  const [customLineQty, setCustomLineQty] = useState('1');

  const [selectedContact, setSelectedContact] = useState<any | null>(null);
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerAddress, setCustomerAddress] = useState('');
  const [customerLocation, setCustomerLocation] = useState<{ latitude: number; longitude: number; address: string; country?: string; city?: string } | null>(null);
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [items, setItems] = useState<QuotationLineItem[]>([]);
  const [tax, setTax] = useState(0);
  const [extraCharges, setExtraCharges] = useState<ExtraCharge[]>([]);
  const [optionalCharges, setOptionalCharges] = useState<{ name: string; amount: number; checked: boolean }[]>([]);
  const [loadingCharges, setLoadingCharges] = useState(false);
  const [shipping, setShipping] = useState(0);
  const [shippingMethod, setShippingMethod] = useState('');
  const [shippingMethodId, setShippingMethodId] = useState('');
  const [shippingMethodSnapshot, setShippingMethodSnapshot] = useState<{ name: string; type: string; price: number; providerName?: string } | undefined>(undefined);
  const [selectedShippingMethod, setSelectedShippingMethod] = useState<any | null>(null);
  const [expressSelected, setExpressSelected] = useState(false);
  const [showShippingMethodModal, setShowShippingMethodModal] = useState(false);
  const [shippingMethods, setShippingMethods] = useState<any[]>([]);
  const [loadingShippingMethods, setLoadingShippingMethods] = useState(false);
  const [manualDiscount, setManualDiscount] = useState(0);
  const [discount, setDiscount] = useState(0);
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState<{ code: string; discountAmount: number } | null>(null);
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  // The business currency by default; the agent can switch it (prices are converted).
  const [currency, setCurrency] = useState(currentBusiness?.currency || 'USD');
  const [changingCurrency, setChangingCurrency] = useState(false);
  const [notes, setNotes] = useState<Note[]>([]);
  const quotationPrefillAppliedRef = useRef(false);


  // ─── Charges / Shipping / Coupon — mirrors AddOrderScreen's exact logic,
  // reusing the same business-config/shipping/coupon endpoints so a quotation
  // computes taxes, charges and delivery pricing identically to a real order,
  // while still letting the agent override the resulting numbers afterward. ───

  const getDeliveryLocation = () => (customerLocation
    ? { latitude: customerLocation.latitude, longitude: customerLocation.longitude, city: customerLocation.city }
    : undefined);

  const calcShippingPrice = (method: any, subtotalAmount: number, useExpress: boolean): number => {
    let base = method.price ?? 0;
    if (method.type === 'free' && method.minOrderAmount > 0 && subtotalAmount < method.minOrderAmount) {
      base = method.belowMinCost != null ? method.belowMinCost : 0;
    }
    if (method.type === 'location_based') {
      base = method._resolvedPrice ?? method.price ?? 0;
    }
    const extra = useExpress && method.expressOption?.enabled ? (method.expressOption.extraCost ?? 0) : 0;
    return base + extra;
  };

  const isFreeThresholdNotMet = (method: any, subtotalAmount: number): boolean =>
    !!method && method.type === 'free' && method.minOrderAmount > 0 && subtotalAmount < method.minOrderAmount;

  const resolveZoneForMethod = async (method: any, location?: { latitude?: number; longitude?: number }): Promise<any> => {
    if (method.type !== 'location_based') return method;
    try {
      const res = await apiService.resolveShippingZone(method._id || method.id, {
        latitude: location?.latitude,
        longitude: location?.longitude,
      });
      if (res.statusCode === 1000 && res.data) {
        return {
          ...method,
          _resolvedPrice: res.data.price,
          _resolvedZone: res.data.zone,
          _zoneMatched: res.data.matched,
          _zoneFallback: res.data.fallback,
        };
      }
    } catch {
      // non-fatal
    }
    return method;
  };

  const loadShippingMethods = async () => {
    try {
      setLoadingShippingMethods(true);
      const response = await apiService.getShippingMethods({ isActive: true, limit: 50 });
      if (response.statusCode === 1000 && response.data) {
        const methods = Array.isArray((response.data as any).methods) ? (response.data as any).methods : Array.isArray(response.data) ? response.data : [];
        setShippingMethods(methods);
      }
    } catch (error) {
      console.error('Failed to load shipping methods:', error);
    } finally {
      setLoadingShippingMethods(false);
    }
  };

  const applyShippingMethod = async (method: any, useExpress: boolean) => {
    let resolved = method;
    if (method.type === 'location_based') {
      resolved = await resolveZoneForMethod(method, customerLocation ? { latitude: customerLocation.latitude, longitude: customerLocation.longitude } : undefined);
      if (!resolved._zoneMatched && !resolved._zoneFallback) {
        showNotification('warning', t('quotations.form.shippingZoneUnavailableTitle'), t('quotations.form.shippingZoneUnavailableMessage'));
        return;
      }
    }
    const price = calcShippingPrice(resolved, subtotal, useExpress);
    const zoneSuffix = resolved._resolvedZone ? ` · ${resolved._resolvedZone.name}` : resolved._zoneFallback ? ` · ${t('quotations.form.otherArea')}` : '';
    const expressSuffix = useExpress && resolved.expressOption?.enabled ? ` (${resolved.expressOption.label || t('quotations.form.express')})` : '';
    setSelectedShippingMethod(resolved);
    setExpressSelected(useExpress);
    setShippingMethodId(resolved._id || resolved.id);
    setShippingMethod(`${resolved.name}${zoneSuffix}${expressSuffix}`);
    setShippingMethodSnapshot({ name: resolved.name, type: resolved.type, price, providerName: resolved.providerName });
    setShipping(price);
  };

  const handleSelectShippingMethod = (method: any) => {
    applyShippingMethod(method, false);
    setShowShippingMethodModal(false);
  };

  const handleToggleExpress = () => {
    if (selectedShippingMethod) applyShippingMethod(selectedShippingMethod, !expressSelected);
  };

  const handleClearShippingMethod = () => {
    setSelectedShippingMethod(null);
    setExpressSelected(false);
    setShippingMethodId('');
    setShippingMethod('');
    setShippingMethodSnapshot(undefined);
    setShipping(0);
  };

  const toggleOptionalCharge = (name: string) => {
    setOptionalCharges((prev) => prev.map((c) => (c.name === name ? { ...c, checked: !c.checked } : c)));
  };

  const applyDiscount = (amount: number) => {
    const couponDiscount = appliedCoupon?.discountAmount ?? 0;
    setManualDiscount(amount);
    setDiscount(amount + couponDiscount);
  };

  const applyCouponCode = async () => {
    if (!couponCode.trim()) return;
    if (!currentBusiness?._id) {
      showNotification('warning', t('common.error'), t('quotations.form.businessNotFound'));
      return;
    }
    setValidatingCoupon(true);
    try {
      const orderItems = items.filter((i) => i.productId && i.productId !== 'external').map((i) => ({ productId: i.productId, totalPrice: i.totalPrice }));
      const res = await apiService.validateCoupon(
        currentBusiness._id,
        couponCode.trim(),
        subtotal,
        selectedContact?._id,
        orderItems.length ? orderItems : undefined,
        getDeliveryLocation(),
      );
      if (res.statusCode === 1000 && res.data?.valid) {
        const couponDiscount = res.data.discountAmount;
        setAppliedCoupon({ code: couponCode.trim().toUpperCase(), discountAmount: couponDiscount });
        setDiscount(manualDiscount + couponDiscount);
        showNotification('success', t('quotations.form.couponAppliedTitle'), t('quotations.form.couponAppliedMessage', { amount: `${currency} ${couponDiscount.toFixed(2)}` }));
      } else {
        showNotification('warning', t('quotations.form.invalidCouponTitle'), res.data?.reason || t('quotations.form.invalidCouponMessage'));
      }
    } catch (error: any) {
      showNotification('error', t('common.error'), error.message);
    } finally {
      setValidatingCoupon(false);
    }
  };

  const recalculateTotals = async (itemsList: QuotationLineItem[], locationOverride?: { latitude?: number; longitude?: number }) => {
    const newSubtotal = itemsList.reduce((sum, it) => sum + it.totalPrice, 0);
    if (selectedShippingMethod) {
      const price = calcShippingPrice(selectedShippingMethod, newSubtotal, expressSelected);
      setShipping(price);
      setShippingMethodSnapshot((prev) => (prev ? { ...prev, price } : prev));
    }
    if (!currentBusiness?._id || itemsList.length === 0) {
      if (!currentBusiness?._id) console.warn('[QuotationFormScreen] recalculateTotals skipped — no currentBusiness in store');
      return;
    }
    setLoadingCharges(true);
    try {
      const productTypes = [...new Set(itemsList.map((i) => i.productType).filter(Boolean))] as ('physical' | 'digital' | 'service')[];
      const orderProductIds = itemsList.map((i) => i.productId).filter(Boolean);
      const res = await apiService.previewCharges({
        subtotal: newSubtotal,
        productTypes: productTypes.length ? productTypes : ['physical'],
        orderProductIds,
        contactId: selectedContact?._id,
        location: locationOverride ?? getDeliveryLocation(),
      });
      if (res.statusCode === 1000 && res.data) {
        const { taxes, taxAmount, charges } = res.data;
        const mandatory = charges.filter((c: any) => c.mandatory !== false);
        const optional = charges.filter((c: any) => c.mandatory === false);
        setOptionalCharges((prev) => optional.map((c: any) => {
          const existing = prev.find((p) => p.name === c.name);
          return { name: c.name, amount: c.amount, checked: existing?.checked ?? false };
        }));
        const mandatoryCharges: ExtraCharge[] = [
          ...taxes.map((tx: any) => ({ name: `${tx.name} (${tx.rate}%)`, amount: tx.amount, _isTax: true })),
          ...mandatory.map((c: any) => ({ name: c.name, amount: c.amount })),
        ];
        setExtraCharges((prev) => {
          const checkedOptional = optional
            .filter((c: any) => prev.some((e) => e.name === c.name && e._optional))
            .map((c: any) => ({ name: c.name, amount: c.amount, _optional: true }));
          return [...mandatoryCharges, ...checkedOptional];
        });
        setTax(taxAmount);
      } else {
        console.warn('[QuotationFormScreen] previewCharges returned no data:', res);
      }
    } catch (error: any) {
      // non-blocking — items/shipping already updated optimistically above
      console.warn('[QuotationFormScreen] previewCharges failed:', error?.message || error);
    } finally {
      setLoadingCharges(false);
    }
  };

  /** Re-resolves per-item pricing against a delivery zone when the customer's
   * location changes — mirrors AddOrderScreen's repriceItemsForLocation,
   * simplified since QuotationLineItem doesn't track addons. */
  const repriceItemsForLocation = async (
    itemsList: QuotationLineItem[],
    location?: { latitude?: number; longitude?: number },
  ): Promise<QuotationLineItem[]> => {
    if (!location?.latitude && !location?.longitude) return itemsList;
    return Promise.all(itemsList.map(async (item) => {
      if (item.productId === 'external' || item.variantId) return item;
      try {
        const res = await apiService.resolveProductPrice(item.productId, {
          latitude: location.latitude,
          longitude: location.longitude,
        });
        if (res.statusCode === 1000 && res.data?.matched) {
          const newUnitPrice = res.data.salePrice ?? res.data.price;
          return { ...item, unitPrice: newUnitPrice, totalPrice: newUnitPrice * item.quantity };
        }
      } catch {
        // non-fatal
      }
      return item;
    }));
  };

  useEffect(() => { if (isEdit) loadQuotation(); }, [quotationId]);

  const loadQuotation = async () => {
    try {
      setLoading(true);
      const response = await apiService.getQuotationById(quotationId!);
      if (response.statusCode === 1000 && response.data) {
        const q = response.data;
        setCustomerName(q.customer?.name || '');
        setCustomerPhone(q.customer?.phone || '');
        setCustomerEmail(q.customer?.email || '');
        setCustomerAddress(q.customer?.address || '');
        if (q.customer?.location?.latitude !== undefined && q.customer?.location?.longitude !== undefined) {
          setCustomerLocation({
            latitude: q.customer.location.latitude,
            longitude: q.customer.location.longitude,
            address: q.customer.location.address || q.customer.address || '',
            country: q.customer.location.country,
            city: q.customer.location.city,
          });
        }
        if (q.contactId) {
          try {
            const contactResponse = await apiService.getParentContactById(q.contactId);
            if (contactResponse.statusCode === 1000 && contactResponse.data) {
              const pc = contactResponse.data;
              setSelectedContact({
                id: pc._id || pc.id,
                _id: pc._id || pc.id,
                name: pc.profile?.name || q.customer?.name || '',
                phoneNumber: pc.profile?.phone || q.customer?.phone,
                phone: pc.profile?.phone || q.customer?.phone,
                email: pc.profile?.email || q.customer?.email,
                address: pc.profile?.location?.address || q.customer?.address,
                location: pc.profile?.location,
              });
            }
          } catch {
            // fall back to the plain customer snapshot below
          }
        }
        if (!q.contactId && q.customer?.name) {
          setSelectedContact({ id: '', name: q.customer.name, phone: q.customer.phone, email: q.customer.email, address: q.customer.address });
        }
        const loadedItems = (q.items || []).map((it: any) => ({
          productId: it.productId,
          variantId: it.variantId,
          variant: it.variant,
          priceUnit: it.product?.priceUnit,
          unitId: it.product?.unitId,
          name: it.product?.name || 'Item',
          sku: it.product?.sku,
          productType: it.product?.productType,
          quantity: it.quantity,
          unitPrice: it.unitPrice,
          totalPrice: it.totalPrice,
        }));
        setItems(loadedItems);
        setTax(q.tax || 0);
        setExtraCharges((q.extraCharges || []).map((c: any) => ({ name: c.name, amount: c.amount, _isTax: /\(\d+(\.\d+)?%\)$/.test(c.name || '') })));
        setShipping(q.shipping || 0);
        if (q.shippingMethodId) {
          setShippingMethodId(q.shippingMethodId);
          setShippingMethod(q.shippingMethod || '');
          setShippingMethodSnapshot(q.shippingMethodSnapshot);
          setSelectedShippingMethod({
            _id: q.shippingMethodId,
            name: q.shippingMethodSnapshot?.name || q.shippingMethod,
            type: q.shippingMethodSnapshot?.type,
            price: q.shippingMethodSnapshot?.price,
            providerName: q.shippingMethodSnapshot?.providerName,
          });
        }
        setManualDiscount(q.manualDiscount ?? q.discount ?? 0);
        setDiscount(q.discount || 0);
        if (q.couponCode) setAppliedCoupon({ code: q.couponCode, discountAmount: (q.discount || 0) - (q.manualDiscount || 0) });
        setCurrency(q.currency || businessCurrency());
        setNotes(parseNotes(q.notes));
        // Refresh tax/mandatory-charges against the live business config —
        // mirrors AddOrderScreen's loadOrder, which recalculates on open too
        // rather than only trusting whatever was stored at save time.
        recalculateTotals(loadedItems);
      } else {
        showNotification('error', t('common.error'), t('quotations.detail.notFound'));
        navigateTo('quotations');
      }
    } catch (error: any) {
      showNotification('error', t('common.error'), error.message || t('quotations.detail.loadError'));
    } finally {
      setLoading(false);
    }
  };

  /** Switch the quotation to another currency, converting everything already priced at the live rate. */
  const handleCurrencyChange = async (next: string) => {
    if (!next || next === currency) return;
    setChangingCurrency(true);
    try {
      const rate = await getExchangeRate(currency, next);
      const conv = (v: number) => Math.round(v * rate * 100) / 100;
      const converted = items.map(item => {
        const unitPrice = item.productId === 'external' && !Number.isFinite(item.unitPrice) ? item.unitPrice : conv(item.unitPrice);
        return { ...item, unitPrice, totalPrice: Math.round(unitPrice * item.quantity * 100) / 100,
          ...(item.variant ? { variant: { ...item.variant, price: unitPrice } } : {}) };
      });
      setCurrency(next);
      setItems(converted);
      setShipping(v => conv(v));
      setManualDiscount(v => conv(v));
      setDiscount(v => conv(v));
      setShippingMethodSnapshot(snapshot => snapshot ? { ...snapshot, price: conv(snapshot.price) } : snapshot);
      setOptionalCharges(charges => charges.map(c => ({ ...c, amount: conv(c.amount) })));
      setAppliedCoupon(coupon => coupon ? { ...coupon, discountAmount: conv(coupon.discountAmount) } : coupon);
      if (converted.length) await recalculateTotals(converted);
    } catch (error: any) {
      showNotification('error', t('common.error'), error.message || t('quotations.form.currencyFailed'));
    } finally {
      setChangingCurrency(false);
    }
  };

  const handleProductsSelected = async (productIds: string[], aiLines?: Array<{ productId: string; variantId?: string; quantity: number; requestedUnit?: string }>) => {
    setShowProductPicker(false);
    try {
      const newItems: QuotationLineItem[] = [];
      for (const [selectionIndex, id] of productIds.entries()) {
        const response = await apiService.getProductById(id);
        if (response.statusCode === 1000 && response.data) {
          const p = response.data;
          const requested = aiLines?.[selectionIndex];
          if (requested && (requested.productId !== id || !Number.isInteger(requested.quantity) || requested.quantity <= 0 || requested.quantity > 10000)) throw new Error('Invalid AI line quantity.');
          const variant = requested?.variantId ? p.variants?.find((v: any) => v.id === requested.variantId) : undefined;
          if (requested?.variantId && !variant) throw new Error('The requested product size is no longer available.');
          const quantity = requested?.quantity ?? 1;
          // Products priced in another currency are converted into the quotation's currency.
          const listed = variant?.price ?? p.salePrice ?? p.price;
          const unitPrice = p.currency && p.currency !== currency && Number.isFinite(listed)
            ? Math.round((await convertAmount(listed, p.currency, currency)) * 100) / 100 : listed;
          newItems.push({ productId: id, name: p.name, sku: p.sku, productType: p.productType, quantity, unitPrice, totalPrice: unitPrice * quantity,
            variantId: variant?.id, variant: variant ? { id: variant.id, name: variant.name, price: unitPrice } : undefined,
            priceUnit: p.priceUnit || requested?.requestedUnit, unitId: p.unitId });
        }
      }
      const updated = [...items, ...newItems];
      setItems(updated);
      recalculateTotals(updated);
    } catch (error: any) {
      showNotification('error', t('common.error'), t('quotations.form.saveError'));
    }
  };

  const aiProductsAppliedRef = useRef(false);
  useEffect(() => {
    const ids = routeParams?.prefillQuotation?.aiProductIds;
    if (isEdit || aiProductsAppliedRef.current || !Array.isArray(ids)) return;
    aiProductsAppliedRef.current = true;
    const lines = routeParams?.prefillQuotation?.aiLines;
    const validIds = ids.filter((id: unknown) => typeof id === 'string' && /^[a-f\d]{24}$/i.test(id)).slice(0, 10);
    void handleProductsSelected(validIds, Array.isArray(lines) ? lines.slice(0, 10) : undefined);
  }, [isEdit, routeParams?.prefillQuotation?.aiProductIds]);

  const handleContactSelect = async (contact: any) => {
    if (!contact) {
      setSelectedContact(null);
      setCustomerName(''); setCustomerPhone(''); setCustomerEmail(''); setCustomerAddress(''); setCustomerLocation(null);
      return;
    }
    setSelectedContact(contact);
    setCustomerName(contact.name || '');
    setCustomerPhone(contact.phoneNumber || contact.phone || '');
    setCustomerEmail(Array.isArray(contact.email) ? (contact.email[0] || '') : (contact.email || ''));
    setCustomerAddress(contact.address || contact.location?.address || '');
    if (contact.location?.latitude !== undefined && contact.location?.longitude !== undefined) {
      const location = {
        latitude: contact.location.latitude,
        longitude: contact.location.longitude,
        address: contact.location.address || contact.address || '',
        country: contact.location.country,
        city: contact.location.city,
      };
      setCustomerLocation(location);
      const repriced = await repriceItemsForLocation(items, location);
      setItems(repriced);
      recalculateTotals(repriced, location);
    } else {
      setCustomerLocation(null);
      recalculateTotals(items);
    }
  };

  useEffect(() => {
    const prefill = routeParams?.prefillQuotation;
    const customer = prefill?.customer || {};
    const location = prefill?.location || customer.location;
    if (isEdit || quotationPrefillAppliedRef.current || !prefill) {
      return;
    }

    quotationPrefillAppliedRef.current = true;
    const contactId = prefill.contactId || customer.id || customer._id;
    const normalizedLocation = location &&
      typeof location.latitude === 'number' &&
      typeof location.longitude === 'number'
        ? {
            latitude: location.latitude,
            longitude: location.longitude,
            address: location.address || `${location.latitude}, ${location.longitude}`,
            country: location.country,
            city: location.city,
          }
        : null;

    if (contactId || customer.name || customer.phone || customer.phoneNumber) {
      setSelectedContact({
        ...customer,
        _id: contactId,
        id: contactId,
        name: customer.name || '',
        phoneNumber: customer.phoneNumber || customer.phone || '',
        phone: customer.phone || customer.phoneNumber || '',
        location: normalizedLocation || undefined,
      });
    }

    setCustomerName(customer.name || '');
    setCustomerPhone(customer.phoneNumber || customer.phone || '');
    setCustomerEmail(Array.isArray(customer.email) ? (customer.email[0] || '') : (customer.email || ''));
    setCustomerAddress(normalizedLocation?.address || customer.address || '');
    setCustomerLocation(normalizedLocation);
    recalculateTotals(items, normalizedLocation || undefined);
    showNotification('success', t('quotations.form.customer'), t('quotations.form.customerPrefilled'));
  }, [isEdit, items, routeParams?.prefillQuotation, showNotification, t]);

  const handleAddCustomLine = () => {
    const price = Number(customLinePrice);
    const qty = Number(customLineQty) || 1;
    if (!customLineName.trim() || !Number.isFinite(price) || price < 0) return;
    const updated = [...items, { productId: 'external', name: customLineName.trim(), quantity: qty, unitPrice: price, totalPrice: price * qty }];
    setItems(updated);
    recalculateTotals(updated);
    setCustomLineName(''); setCustomLinePrice(''); setCustomLineQty('1');
    setShowCustomLineForm(false);
  };

  const updateItem = (index: number, patch: Partial<QuotationLineItem>) => {
    const updated = items.map((it, i) => {
      if (i !== index) return it;
      const merged = { ...it, ...patch };
      merged.totalPrice = merged.unitPrice * merged.quantity;
      return merged;
    });
    setItems(updated);
    recalculateTotals(updated);
  };

  const removeItem = (index: number) => {
    const updated = items.filter((_, i) => i !== index);
    setItems(updated);
    recalculateTotals(updated);
  };

  const subtotal = items.reduce((sum, it) => sum + it.totalPrice, 0);
  const chargesTotal = extraCharges.filter((c) => !c._isTax).reduce((sum, c) => sum + c.amount, 0);
  const totalPrice = Math.max(0, subtotal + tax + chargesTotal + shipping - discount);

  const handleSave = async () => {
    if (!customerName.trim() || !customerPhone.trim()) {
      showNotification('warning', t('quotations.form.missingInfoTitle'), t('quotations.form.missingInfoMessage'));
      return;
    }
    if (items.length === 0) {
      showNotification('warning', t('quotations.form.noItemsTitle'), t('quotations.form.noItemsMessage'));
      return;
    }
    try {
      setSaving(true);
      const validNotes = notes.filter((note) => note && note.content && note.content.trim());
      const payload = {
        contactId: selectedContact?._id || selectedContact?.id || undefined,
        customer: {
          name: customerName.trim(),
          phone: customerPhone.trim(),
          email: customerEmail.trim() || undefined,
          address: customerAddress.trim() || undefined,
          location: customerLocation || undefined,
        },
        items: items.map((it) => ({
          productId: it.productId,
          variantId: it.variantId,
          variant: it.variant,
          quantity: it.quantity,
          product: { _id: it.productId, name: it.name, price: it.unitPrice, sku: it.sku, productType: it.productType, currency, priceUnit: it.priceUnit, unitId: it.unitId },
          unitPrice: it.unitPrice,
          totalPrice: it.totalPrice,
        })),
        subtotal,
        tax,
        extraCharges: extraCharges.filter((c) => !c._optional || optionalCharges.some((o) => o.name === c.name && o.checked)),
        shipping,
        shippingMethod: shippingMethod || undefined,
        shippingMethodId: shippingMethodId || undefined,
        shippingMethodSnapshot,
        discount,
        manualDiscount,
        couponCode: appliedCoupon?.code,
        totalPrice,
        currency,
        notes: validNotes.length > 0 ? JSON.stringify(validNotes) : undefined,
      };

      const response = isEdit
        ? await apiService.updateQuotation(quotationId!, payload)
        : await apiService.createQuotation({
            ...(payload as any),
            // Started from a visit in the contact profile — logged on that visit.
            ...(routeParams?.prefillQuotation?.visitId ? { visitId: routeParams.prefillQuotation.visitId } : {}),
          });

      if (response.statusCode === 1000 && response.data) {
        showNotification('success', t('quotations.form.savedTitle'), isEdit ? t('quotations.form.updatedMessage') : t('quotations.form.createdMessage'));
        navigateTo('quotation-detail', response.data._id || quotationId);
      } else {
        showNotification('error', t('common.error'), response.errors?.[0]?.message || t('quotations.form.saveError'));
      }
    } catch (error: any) {
      showNotification('error', t('common.error'), error.message || t('quotations.form.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleTabPress = createTabPressHandler(navigateTo, setActiveTab);

  return (
    <ScreenLayout
      header={
        <Header showNotifications showMessages onNotificationPress={() => navigateTo('notifications')} onMessagePress={() => navigateTo('chat')} showNavigation={Platform.OS === 'web'} activeTab={activeTab} onTabPress={handleTabPress} />
      }
      showBottomTabs={Platform.OS !== 'web'}
      onLeftPaneClose={() => {}}
      leftPaneContent="settings"
      onSettingsItemPress={async (itemId) => {
        const { setPendingSettingsItem } = await import('../utils/tempStorage');
        await setPendingSettingsItem(itemId);
        navigateTo('settings');
      }}
      onRightPaneClose={() => {}}
      formHeader={
        <FormHeader
          title={isEdit ? t('quotations.form.editTitle') : t('quotations.form.newTitle')}
          onBack={() => navigateTo(isEdit ? 'quotation-detail' : 'quotations', quotationId)}
          actions={[{ icon: 'checkmark', primary: true, onPress: handleSave, loading: saving, disabled: saving, accessibilityLabel: t('common.save') }]}
        />
      }
    >
      <View style={styles.container}>
        {loading ? (
          <View style={styles.centerContainer}><ActivityIndicator size="large" color={colors.primary} /></View>
        ) : (
          <ScrollView style={styles.content} contentContainerStyle={[styles.contentInner, phoneFormGutter]}>
            <Text style={[styles.sectionTitle, { textAlign: rtl.textStart }]}>{t('quotations.form.customer')}</Text>
            <View style={styles.contactSelectorContainer}>
              <ContactSelector
                mode="edit"
                variant="simple"
                contact={selectedContact}
                onContactSelect={handleContactSelect}
                onAddNewContact={() => navigateTo('contacts')}
                showPreviousEvents={false}
                showPreviousOrders={false}
              />
            </View>
            <Input label={t('quotations.form.email')} value={customerEmail} onChangeText={setCustomerEmail} placeholder={t('quotations.form.emailPlaceholder')} latin autoCapitalize="none" />
            <Input label={t('quotations.form.address')} value={customerAddress} onChangeText={setCustomerAddress} placeholder={t('quotations.form.addressPlaceholder')} multiline />

            <Text style={[styles.fieldLabel, { textAlign: rtl.textStart }]}>{t('quotations.form.location')} <Text style={styles.fieldHint}>{t('quotations.form.locationHint')}</Text></Text>
            <TouchableOpacity style={styles.locationButton} onPress={() => setShowLocationPicker(true)}>
              {customerLocation ? (
                <View style={[styles.locationInfo, { flexDirection: rtl.rowDirection }]}>
                  <Ionicons name="location" size={20} color={colors.primary} />
                  <View style={styles.locationTextContainer}>
                    <Text style={styles.locationAddress} numberOfLines={1}>{customerLocation.address}</Text>
                    <Text style={styles.locationCoordinates}>{customerLocation.latitude.toFixed(6)}, {customerLocation.longitude.toFixed(6)}</Text>
                  </View>
                  <Ionicons name={rtl.mirrorIcon('chevron-forward') as any} size={20} color={colors.textLight} />
                </View>
              ) : (
                <View style={[styles.locationPlaceholder, { flexDirection: rtl.rowDirection }]}>
                  <Ionicons name="location-outline" size={20} color={colors.textLight} />
                  <Text style={styles.locationPlaceholderText}>{t('quotations.form.selectLocation')}</Text>
                  <Ionicons name={rtl.mirrorIcon('chevron-forward') as any} size={20} color={colors.textLight} />
                </View>
              )}
            </TouchableOpacity>

            <View style={[styles.sectionHeader, { flexDirection: rtl.rowDirection, marginTop: spacing.lg }]}>
              <Text style={[styles.sectionTitle, { textAlign: rtl.textStart }]}>{t('quotations.form.items')}</Text>
            </View>
            <View style={[styles.itemActionsRow, { flexDirection: rtl.rowDirection }]}>
              <TouchableOpacity style={[styles.itemActionButton, { flexDirection: rtl.rowDirection }]} onPress={() => setShowProductPicker(true)}>
                <Ionicons name="cube-outline" size={16} color={colors.primary} />
                <Text style={styles.itemActionText}>{t('quotations.form.addProduct')}</Text>
              </TouchableOpacity>
              <TouchableOpacity style={[styles.itemActionButton, { flexDirection: rtl.rowDirection }]} onPress={() => setShowCustomLineForm((v) => !v)}>
                <Ionicons name="create-outline" size={16} color={colors.primary} />
                <Text style={styles.itemActionText}>{t('quotations.form.addCustomLine')}</Text>
              </TouchableOpacity>
            </View>

            {showCustomLineForm && (
              <View style={styles.customLineForm}>
                <Input placeholder={t('quotations.form.lineDescriptionPlaceholder')} value={customLineName} onChangeText={setCustomLineName} containerStyle={styles.customLineInput} />
                <View style={[styles.customLineRow, { flexDirection: rtl.rowDirection }]}>
                  <Input placeholder={t('quotations.form.pricePlaceholder')} value={customLinePrice} onChangeText={setCustomLinePrice} keyboardType="numeric" containerStyle={[styles.customLineInput, { flex: 1 }]} inputStyle={styles.compactInput} />
                  <Input placeholder={t('quotations.form.qtyPlaceholder')} value={customLineQty} onChangeText={setCustomLineQty} keyboardType="numeric" containerStyle={[styles.customLineInput, { flex: 1 }]} inputStyle={styles.compactInput} />
                </View>
                <Button title={t('quotations.form.addLine')} onPress={handleAddCustomLine} variant="primary" />
              </View>
            )}

            {items.length === 0 ? (
              <Text style={styles.emptyText}>{t('quotations.form.noItemsYet')}</Text>
            ) : (
              items.map((item, index) => (
                <View key={index} style={styles.itemCard}>
                  <View style={[styles.itemHeader, { flexDirection: rtl.rowDirection }]}>
                    <Text style={[styles.itemName, { textAlign: rtl.textStart }]} numberOfLines={1}>{item.name}{item.variant?.name ? ` — ${item.variant.name}` : ''}{item.sku ? ` (${item.sku})` : ''}{item.priceUnit ? ` (${item.priceUnit})` : ''}</Text>
                    <TouchableOpacity onPress={() => removeItem(index)}>
                      <Ionicons name="close-circle-outline" size={20} color={colors.error} />
                    </TouchableOpacity>
                  </View>
                  <View style={[styles.itemFieldsRow, { flexDirection: rtl.rowDirection }]}>
                    <View style={styles.itemField}>
                      <Text style={styles.itemFieldLabel}>{t('quotations.form.qty')}</Text>
                      <Input value={String(item.quantity)} onChangeText={(v) => updateItem(index, { quantity: Number(v) || 1 })} keyboardType="numeric" containerStyle={styles.itemFieldInput} inputStyle={styles.compactInput} />
                    </View>
                    <View style={styles.itemField}>
                      <Text style={styles.itemFieldLabel}>{t('quotations.form.unitPrice')}</Text>
                      <Input value={String(item.unitPrice)} onChangeText={(v) => updateItem(index, { unitPrice: Number(v) || 0 })} keyboardType="numeric" containerStyle={styles.itemFieldInput} inputStyle={styles.compactInput} />
                    </View>
                    <View style={styles.itemField}>
                      <Text style={styles.itemFieldLabel}>{t('quotations.form.total')}</Text>
                      <Text style={styles.itemTotalValue}>{currency} {item.totalPrice.toFixed(2)}</Text>
                    </View>
                  </View>
                </View>
              ))
            )}

            <Text style={[styles.sectionTitle, { textAlign: rtl.textStart, marginTop: spacing.lg }]}>{t('quotations.form.charges')}</Text>

            <Text style={[styles.fieldLabel, { textAlign: rtl.textStart }]}>{t('quotations.form.shippingMethod')}</Text>
            {shippingMethodId ? (
              <View>
                <View style={[styles.shippingMethodSelected, { flexDirection: rtl.rowDirection }]}>
                  <View style={styles.shippingMethodInfo}>
                    <Text style={styles.shippingMethodName}>{shippingMethod}</Text>
                    <Text style={styles.shippingMethodPrice}>
                      {shippingMethodSnapshot?.price === 0 ? t('quotations.form.free') : `${currency} ${(shippingMethodSnapshot?.price || 0).toFixed(2)}`}
                    </Text>
                  </View>
                  <View style={[styles.shippingMethodActions, { flexDirection: rtl.rowDirection }]}>
                    <TouchableOpacity onPress={() => { loadShippingMethods(); setShowShippingMethodModal(true); }} style={styles.shippingMethodBtn}>
                      <Ionicons name="swap-horizontal-outline" size={18} color={colors.primary} />
                    </TouchableOpacity>
                    <TouchableOpacity onPress={handleClearShippingMethod} style={styles.shippingMethodBtn}>
                      <Ionicons name="close-circle-outline" size={18} color={colors.error} />
                    </TouchableOpacity>
                  </View>
                </View>
                {selectedShippingMethod?.type === 'location_based' && (selectedShippingMethod?._zoneMatched || selectedShippingMethod?._zoneFallback) && (
                  <Text style={styles.thresholdText}>
                    {selectedShippingMethod._resolvedZone ? t('quotations.form.zoneLabel', { zone: selectedShippingMethod._resolvedZone.name }) : t('quotations.form.otherAreaFallback')}
                  </Text>
                )}
                {isFreeThresholdNotMet(selectedShippingMethod, subtotal) && (
                  <Text style={styles.thresholdText}>
                    {t('quotations.form.freeShippingHint', { amount: `${currency} ${((selectedShippingMethod.minOrderAmount - subtotal) || 0).toFixed(2)}` })}
                  </Text>
                )}
                {selectedShippingMethod?.expressOption?.enabled && (
                  <TouchableOpacity
                    style={[styles.expressToggle, { flexDirection: rtl.rowDirection }, expressSelected && styles.expressToggleActive]}
                    onPress={handleToggleExpress}
                  >
                    <Ionicons name="flash-outline" size={15} color={expressSelected ? colors.white : colors.primary} />
                    <Text style={[styles.expressToggleText, expressSelected && styles.expressToggleTextActive]}>
                      {selectedShippingMethod.expressOption.label || t('quotations.form.express')} +{currency} {(selectedShippingMethod.expressOption.extraCost || 0).toFixed(2)}
                    </Text>
                    {expressSelected && <Ionicons name="checkmark" size={14} color={colors.white} />}
                  </TouchableOpacity>
                )}
              </View>
            ) : (
              <TouchableOpacity style={[styles.shippingMethodPicker, { flexDirection: rtl.rowDirection }]} onPress={() => { loadShippingMethods(); setShowShippingMethodModal(true); }}>
                <Ionicons name="car-outline" size={18} color={colors.textLight} />
                <Text style={styles.shippingMethodPlaceholder}>{t('quotations.form.selectShippingMethod')}</Text>
                <Ionicons name={rtl.mirrorIcon('chevron-forward') as any} size={16} color={colors.textLight} />
              </TouchableOpacity>
            )}

            {optionalCharges.length > 0 && (
              <View style={{ marginTop: spacing.sm }}>
                {optionalCharges.map((c) => (
                  <TouchableOpacity key={c.name} style={[styles.checkboxRow, { flexDirection: rtl.rowDirection }]} onPress={() => toggleOptionalCharge(c.name)}>
                    <View style={[styles.checkbox, c.checked && styles.checkboxChecked]} />
                    <Text style={styles.checkboxLabel}>{c.name} (+{currency} {c.amount.toFixed(2)})</Text>
                  </TouchableOpacity>
                ))}
              </View>
            )}

            {loadingCharges && <ActivityIndicator size="small" color={colors.primary} style={{ marginTop: spacing.sm }} />}

            <Input
              label={t('quotations.form.manualDiscount')}
              value={String(manualDiscount || '')}
              onChangeText={(v) => applyDiscount(Number(v) || 0)}
              keyboardType="numeric"
              containerStyle={{ marginTop: spacing.md }}
            />
            <View style={[styles.couponRow, { flexDirection: rtl.rowDirection }]}>
              <View style={[styles.couponInputWrap, { flexDirection: rtl.rowDirection }]}>
                <Ionicons name="pricetag-outline" size={16} color={colors.textLight} />
                <TextInput
                  style={styles.couponInput}
                  value={couponCode}
                  onChangeText={setCouponCode}
                  placeholder={t('quotations.form.couponCodePlaceholder')}
                  placeholderTextColor={colors.textLight}
                  autoCapitalize="characters"
                  editable={!appliedCoupon}
                />
              </View>
              {appliedCoupon ? (
                <TouchableOpacity onPress={() => { setAppliedCoupon(null); setDiscount(manualDiscount); setCouponCode(''); }}>
                  <Ionicons name="close-circle" size={20} color={colors.error} />
                </TouchableOpacity>
              ) : (
                <TouchableOpacity onPress={applyCouponCode} disabled={validatingCoupon || !couponCode.trim()}>
                  {validatingCoupon ? <ActivityIndicator size="small" color={colors.primary} /> : <Text style={styles.couponApplyText}>{t('quotations.form.apply')}</Text>}
                </TouchableOpacity>
              )}
            </View>

            <View style={{ marginTop: spacing.md, opacity: changingCurrency ? 0.6 : 1 }}>
              <CurrencyPicker label={t('quotations.form.currency')} value={currency} onChange={(next) => void handleCurrencyChange(next)} />
            </View>

            <View style={styles.totalsCard}>
              <View style={[styles.totalsRow, { flexDirection: rtl.rowDirection }]}><Text style={styles.totalsLabel}>{t('quotations.form.subtotal')}</Text><Text style={styles.totalsValue}>{currency} {subtotal.toFixed(2)}</Text></View>
              {!!tax && (
                <View style={[styles.totalsRow, { flexDirection: rtl.rowDirection }]}><Text style={styles.totalsLabel}>{t('quotations.form.tax')}</Text><Text style={styles.totalsValue}>{currency} {tax.toFixed(2)}</Text></View>
              )}
              {extraCharges.filter((c) => !c._isTax && (!c._optional || optionalCharges.some((o) => o.name === c.name && o.checked))).map((c) => (
                <View key={c.name} style={[styles.totalsRow, { flexDirection: rtl.rowDirection }]}><Text style={styles.totalsLabel}>{c.name}</Text><Text style={styles.totalsValue}>{currency} {c.amount.toFixed(2)}</Text></View>
              ))}
              {!!shipping && (
                <View style={[styles.totalsRow, { flexDirection: rtl.rowDirection }]}><Text style={styles.totalsLabel}>{t('quotations.form.shipping')}</Text><Text style={styles.totalsValue}>{currency} {shipping.toFixed(2)}</Text></View>
              )}
              {!!discount && (
                <View style={[styles.totalsRow, { flexDirection: rtl.rowDirection }]}><Text style={styles.totalsLabel}>{t('quotations.form.discount')}</Text><Text style={styles.totalsValue}>-{currency} {discount.toFixed(2)}</Text></View>
              )}
              <View style={[styles.totalsRow, styles.totalsRowGrand, { flexDirection: rtl.rowDirection }]}><Text style={styles.totalsLabelGrand}>{t('quotations.form.total')}</Text><Text style={styles.totalsValueGrand}>{currency} {totalPrice.toFixed(2)}</Text></View>
            </View>

            <View style={{ marginTop: spacing.lg }}>
              <NotesSection
                notes={notes}
                onAddNote={(content) => {
                  if (!content.trim()) return;
                  setNotes((prev) => [...prev, {
                    content: content.trim(),
                    createdBy: {
                      id: user?.id || '',
                      _id: user?.id || '',
                      firstName: user?.firstName,
                      lastName: user?.lastName,
                      email: user?.email,
                      avatar: user?.avatar,
                      profilePicture: user?.profilePicture,
                    },
                    createdAt: new Date(),
                  }]);
                }}
                onDeleteNote={(noteId) => {
                  setNotes((prev) => prev.filter((note, index) => (
                    typeof noteId === 'string' ? note._id !== noteId : index !== noteId
                  )));
                }}
                placeholder={t('quotations.form.notesPlaceholder')}
                title={t('quotations.detail.notes')}
                editable
                currentUserId={user?.id}
              />
            </View>
          </ScrollView>
        )}
      </View>

      <ProductSelectorModal
        visible={showProductPicker}
        onClose={() => setShowProductPicker(false)}
        onConfirm={handleProductsSelected}
        title={t('quotations.form.addProducts')}
        confirmText={t('productSelector.addSelected')}
        allowMultiple
      />

      <MapLocationPicker
        visible={showLocationPicker}
        onClose={() => setShowLocationPicker(false)}
        onLocationSelect={async (location) => {
          setCustomerLocation(location);
          setCustomerAddress(location.address || customerAddress);
          setShowLocationPicker(false);
          if (selectedShippingMethod?.type === 'location_based') {
            await applyShippingMethod(selectedShippingMethod, expressSelected);
          }
          const repriced = await repriceItemsForLocation(items, location);
          setItems(repriced);
          recalculateTotals(repriced, location);
        }}
        initialLocation={customerLocation}
      />

      <Modal visible={showShippingMethodModal} animationType="slide" transparent onRequestClose={() => setShowShippingMethodModal(false)}>
        <View style={styles.shippingModalOverlay}>
          <TouchableOpacity style={styles.shippingModalBackdrop} activeOpacity={1} onPress={() => setShowShippingMethodModal(false)} />
          <View style={styles.shippingModalContent}>
            <View style={[styles.shippingModalHeader, { flexDirection: rtl.rowDirection }]}>
              <Text style={styles.shippingModalTitle}>{t('quotations.form.selectShippingMethodTitle')}</Text>
              <TouchableOpacity onPress={() => setShowShippingMethodModal(false)}>
                <Ionicons name="close" size={24} color={colors.textDark} />
              </TouchableOpacity>
            </View>
            {loadingShippingMethods ? (
              <View style={styles.centerContainer}><ActivityIndicator size="large" color={colors.primary} /></View>
            ) : shippingMethods.length === 0 ? (
              <Text style={styles.emptyText}>{t('quotations.form.noShippingMethods')}</Text>
            ) : (
              <FlatList
                data={shippingMethods}
                keyExtractor={(item) => item._id || item.id}
                renderItem={({ item }) => {
                  const effectivePrice = item.type === 'free' && item.minOrderAmount && subtotal < item.minOrderAmount
                    ? (item.belowMinCost ?? 0)
                    : (item.price ?? 0);
                  return (
                    <TouchableOpacity style={[styles.shippingModalRow, { flexDirection: rtl.rowDirection }]} onPress={() => handleSelectShippingMethod(item)}>
                      <Ionicons name={item.type === 'free' ? 'gift-outline' : item.type === 'location_based' ? 'location-outline' : 'car-outline'} size={20} color={colors.primary} />
                      <View style={{ flex: 1 }}>
                        <Text style={styles.shippingModalRowName}>{item.name}</Text>
                        {!!item.description && <Text style={styles.shippingModalRowDesc} numberOfLines={1}>{item.description}</Text>}
                      </View>
                      <Text style={styles.shippingModalRowPrice}>
                        {item.type === 'location_based' ? '' : effectivePrice === 0 ? t('quotations.form.free') : `${currency} ${effectivePrice.toFixed(2)}`}
                      </Text>
                    </TouchableOpacity>
                  );
                }}
              />
            )}
          </View>
        </View>
      </Modal>
    </ScreenLayout>
  );
};

const getStyles = () => StyleSheet.create({
  container: { flex: 1, backgroundColor: colors.background.base },
  centerContainer: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  content: { flex: 1 },
  contentInner: { padding: spacing.lg, paddingBottom: Platform.OS !== 'web' ? 120 : spacing.xl },
  sectionTitle: { fontSize: typography.fontSize.lg, fontWeight: '600', color: colors.textDark, fontFamily: typography.fontFamily.semibold },
  fieldLabel: { fontSize: typography.fontSize.sm, color: colors.textDark, fontFamily: typography.fontFamily.medium, marginBottom: spacing.xs },
  fieldHint: { fontSize: typography.fontSize.xs, color: colors.textLight },
  locationButton: {
    backgroundColor: colors.lightGray,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: designTokens.borderRadius.md,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    marginBottom: spacing.md,
  },
  locationInfo: { alignItems: 'center', gap: spacing.sm },
  locationTextContainer: { flex: 1, minWidth: 0 },
  locationAddress: { fontSize: typography.fontSize.base, color: colors.textDark, marginBottom: spacing.xs / 2, fontFamily: typography.fontFamily.regular },
  locationCoordinates: { fontSize: typography.fontSize.sm, color: colors.textSecondary, fontFamily: typography.fontFamily.regular },
  locationPlaceholder: { alignItems: 'center', gap: spacing.sm },
  locationPlaceholderText: { fontSize: typography.fontSize.base, color: colors.textLight, flex: 1, fontFamily: typography.fontFamily.regular },
  contactSelectorContainer: {
    zIndex: 99999,
    position: 'relative' as any,
    overflow: 'visible' as any,
    marginBottom: spacing.md,
    ...(Platform.OS === 'web' && { isolation: 'isolate' as any }),
    ...(Platform.OS !== 'web' && { elevation: 50 }),
  },
  sectionHeader: { alignItems: 'center', justifyContent: 'space-between' },
  itemActionsRow: { gap: spacing.sm, marginTop: spacing.sm, marginBottom: spacing.sm },
  itemActionButton: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs, paddingVertical: spacing.xs, paddingHorizontal: spacing.sm, borderRadius: designTokens.borderRadius.sm, borderWidth: 1, borderColor: colors.border },
  itemActionText: { fontSize: typography.fontSize.sm, color: colors.primary, fontFamily: typography.fontFamily.medium },
  customLineForm: { backgroundColor: colors.background.surface, borderRadius: designTokens.borderRadius.md, borderWidth: 1, borderColor: colors.border, padding: spacing.md, marginBottom: spacing.md, gap: spacing.sm },
  customLineInput: { marginBottom: 0 },
  customLineRow: { gap: spacing.sm },
  emptyText: { fontSize: typography.fontSize.sm, color: colors.textLight, marginTop: spacing.sm, marginBottom: spacing.sm },
  itemCard: { backgroundColor: colors.background.surface, borderRadius: designTokens.borderRadius.md, borderWidth: 1, borderColor: colors.border, padding: spacing.sm, marginBottom: spacing.sm },
  itemHeader: { alignItems: 'center', justifyContent: 'space-between', gap: spacing.sm, marginBottom: spacing.xs },
  itemName: { flex: 1, fontSize: typography.fontSize.sm, fontWeight: '600', color: colors.textDark },
  itemFieldsRow: { gap: spacing.sm, flexWrap: 'wrap' },
  itemField: { flexGrow: 1, flexShrink: 1, flexBasis: 0, minWidth: 0 },
  // Input defaults to minWidth 200 on web; these sit three-to-a-row.
  compactInput: { minWidth: 0, width: '100%' },
  itemFieldLabel: { fontSize: typography.fontSize.xs, color: colors.textLight, marginBottom: 2 },
  itemFieldInput: { marginBottom: 0 },
  itemTotalValue: { fontSize: typography.fontSize.sm, fontWeight: '600', color: colors.textDark, paddingVertical: spacing.sm },
  chargesRow: { gap: spacing.sm, flexWrap: 'wrap' },
  chargeInput: { flexGrow: 1, flexBasis: 90, minWidth: 90 },
  totalsCard: { backgroundColor: colors.background.surface, borderRadius: designTokens.borderRadius.md, borderWidth: 1, borderColor: colors.border, padding: spacing.md, marginTop: spacing.md },
  totalsRow: { justifyContent: 'space-between', paddingVertical: spacing.xs },
  totalsLabel: { fontSize: typography.fontSize.sm, color: colors.textLight },
  totalsValue: { fontSize: typography.fontSize.sm, color: colors.textDark },
  totalsRowGrand: { borderTopWidth: 1, borderTopColor: colors.border, marginTop: spacing.xs, paddingTop: spacing.sm },
  totalsLabelGrand: { fontSize: typography.fontSize.base, fontWeight: '700', color: colors.primary },
  totalsValueGrand: { fontSize: typography.fontSize.base, fontWeight: '700', color: colors.primary },

  // Shipping method
  shippingMethodPicker: { alignItems: 'center', gap: spacing.sm, borderWidth: 1, borderColor: colors.border, borderRadius: designTokens.borderRadius.md, paddingHorizontal: spacing.sm, paddingVertical: spacing.sm, backgroundColor: colors.lightGray },
  shippingMethodPlaceholder: { flex: 1, fontSize: typography.fontSize.base, color: colors.textLight },
  shippingMethodSelected: { alignItems: 'center', justifyContent: 'space-between', borderWidth: 1, borderColor: colors.border, borderRadius: designTokens.borderRadius.md, padding: spacing.sm, backgroundColor: colors.background.surface },
  shippingMethodInfo: { flex: 1 },
  shippingMethodName: { fontSize: typography.fontSize.sm, fontWeight: '600', color: colors.textDark },
  shippingMethodPrice: { fontSize: typography.fontSize.xs, color: colors.textLight, marginTop: 2 },
  shippingMethodActions: { alignItems: 'center', gap: spacing.sm },
  shippingMethodBtn: { padding: spacing.xs },
  thresholdText: { fontSize: typography.fontSize.xs, color: colors.textLight, marginTop: spacing.xs },
  expressToggle: { alignItems: 'center', alignSelf: 'flex-start', gap: spacing.xs, paddingHorizontal: spacing.sm, paddingVertical: spacing.xs, borderRadius: designTokens.borderRadius.sm, borderWidth: 1, borderColor: colors.primary, marginTop: spacing.sm },
  expressToggleActive: { backgroundColor: colors.primary },
  expressToggleText: { fontSize: typography.fontSize.xs, color: colors.primary, fontFamily: typography.fontFamily.medium },
  expressToggleTextActive: { color: colors.white },

  // Optional charges
  checkboxRow: { alignItems: 'center', gap: spacing.sm, marginBottom: spacing.xs },
  checkbox: { width: 18, height: 18, borderRadius: 4, borderWidth: 2, borderColor: colors.border },
  checkboxChecked: { backgroundColor: colors.primary, borderColor: colors.primary },
  checkboxLabel: { fontSize: typography.fontSize.sm, color: colors.textDark },

  // Coupon
  couponRow: { alignItems: 'center', gap: spacing.sm, marginTop: spacing.sm },
  couponInputWrap: { flex: 1, alignItems: 'center', gap: spacing.xs, borderWidth: 1, borderColor: colors.border, borderRadius: designTokens.borderRadius.md, paddingHorizontal: spacing.sm },
  couponInput: { flex: 1, paddingVertical: spacing.sm, fontSize: typography.fontSize.sm, color: colors.textDark },
  couponApplyText: { fontSize: typography.fontSize.sm, color: colors.primary, fontFamily: typography.fontFamily.semibold, paddingHorizontal: spacing.sm },

  // Shipping method modal
  shippingModalOverlay: { flex: 1, justifyContent: 'flex-end', backgroundColor: 'rgba(0,0,0,0.5)' },
  shippingModalBackdrop: { ...StyleSheet.absoluteFillObject },
  shippingModalContent: { backgroundColor: colors.background.surface, borderTopLeftRadius: designTokens.borderRadius.lg, borderTopRightRadius: designTokens.borderRadius.lg, maxHeight: '70%', padding: spacing.md },
  shippingModalHeader: { alignItems: 'center', justifyContent: 'space-between', marginBottom: spacing.md },
  shippingModalTitle: { fontSize: typography.fontSize.lg, fontWeight: '600', color: colors.textDark },
  shippingModalRow: { alignItems: 'center', gap: spacing.sm, paddingVertical: spacing.sm, borderBottomWidth: 1, borderBottomColor: colors.border },
  shippingModalRowName: { fontSize: typography.fontSize.sm, fontWeight: '600', color: colors.textDark },
  shippingModalRowDesc: { fontSize: typography.fontSize.xs, color: colors.textLight, marginTop: 2 },
  shippingModalRowPrice: { fontSize: typography.fontSize.sm, fontWeight: '600', color: colors.primary },
});
